import { NavLink, Outlet, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useDrive } from "../context/DriveContext";
import {
  Receipt,
  Wallet,
  Truck,
  Student,
  PlusSquare,
  Folders,
  GearSix,
  SignOut,
  CloudCheck,
  CloudSlash,
  User,
  ListMagnifyingGlass,
  ShieldCheck,
} from "@phosphor-icons/react";
import { useState } from "react";
import ConfirmDialog from "./ConfirmDialog";

const NAV_SECTIONS = [
  {
    label: "Финансы",
    adminOnly: true,
    items: [
      { to: "/", label: "Счета за сайты", icon: Receipt, testid: "nav-subscriptions", end: true, adminOnly: true },
      { to: "/expenses", label: "Ежемесячные расходы", icon: Wallet, testid: "nav-expenses", adminOnly: true },
    ],
  },
  {
    items: [
      { to: "/shipments", label: "Поставки", icon: Truck, testid: "nav-shipments" },
    ],
  },
  {
    label: "Семья",
    adminOnly: true,
    items: [
      { to: "/children", label: "Дети", icon: Student, testid: "nav-children", adminOnly: true },
    ],
  },
  {
    label: "Прайсы",
    adminOnly: true,
    items: [
      { to: "/prices/add", label: "Добавить позицию", icon: PlusSquare, testid: "nav-prices-add", adminOnly: true },
      { to: "/prices/files", label: "Реестр файлов", icon: Folders, testid: "nav-prices-files", adminOnly: true },
    ],
  },
];

export default function Shell() {
  const { user, logout, logoutAllDevices } = useAuth();
  const { connected, checking } = useDrive();
  const isAdmin = !!user?.is_admin;
  const [confirmRevokeAll, setConfirmRevokeAll] = useState(false);

  const sections = NAV_SECTIONS.filter((s) => !s.adminOnly || isAdmin).map((s) => ({
    ...s,
    items: s.items.filter((i) => !i.adminOnly || isAdmin),
  })).filter((s) => s.items.length > 0);

  return (
    <div className="min-h-screen flex bg-[#f5f5f4] text-[#0a0a0a]">
      {/* Sidebar */}
      <aside
        className="w-64 shrink-0 border-r border-[#d4d4d8] bg-white flex flex-col sticky top-0 h-screen self-start"
        data-testid="sidebar"
      >
        <nav className="flex-1 py-2 overflow-y-auto">
          {sections.map((section) => (
            <div key={section.label || "_"} className="mb-2">
              {section.label && (
                <div className="px-5 pt-3 pb-1">
                  <div className="mono-label text-[10px]">{section.label}</div>
                </div>
              )}
              {section.items.map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    data-testid={item.testid}
                    className={({ isActive }) =>
                      `flex items-center justify-between px-5 py-2 text-sm transition-colors duration-150 ${
                        isActive
                          ? "bg-[#0047FF] text-white"
                          : "text-[#0a0a0a] hover:bg-[#0a0a0a] hover:text-white"
                      }`
                    }
                  >
                    <span className="flex items-center gap-2.5 min-w-0">
                      <Icon size={15} weight="bold" className="shrink-0" />
                      <span className="truncate">{item.label}</span>
                    </span>
                  </NavLink>
                );
              })}
            </div>
          ))}

          {isAdmin && (
            <div className="mb-2">
              <div className="px-5 pt-3 pb-1">
                <div className="mono-label text-[10px]">Система</div>
              </div>
              <NavLink
                to="/audit"
                data-testid="nav-audit"
                className={({ isActive }) =>
                  `flex items-center gap-2.5 px-5 py-2 text-sm transition-colors duration-150 ${
                    isActive
                      ? "bg-[#0047FF] text-white"
                      : "text-[#0a0a0a] hover:bg-[#0a0a0a] hover:text-white"
                  }`
                }
              >
                <ListMagnifyingGlass size={15} weight="bold" />
                <span>Журнал действий</span>
              </NavLink>
              <NavLink
                to="/settings"
                data-testid="nav-settings"
                className={({ isActive }) =>
                  `flex items-center gap-2.5 px-5 py-2 text-sm transition-colors duration-150 ${
                    isActive
                      ? "bg-[#0047FF] text-white"
                      : "text-[#0a0a0a] hover:bg-[#0a0a0a] hover:text-white"
                  }`
                }
              >
                <GearSix size={15} weight="bold" />
                <span>Настройки</span>
              </NavLink>
            </div>
          )}
        </nav>

        {/* Drive status — only admin sees it */}
        {isAdmin && (
          <div className="border-t border-[#d4d4d8] p-3" data-testid="sidebar-drive-status">
            {checking ? (
              <div className="px-3 py-2 text-xs text-[#52525b] font-mono">проверка...</div>
            ) : connected ? (
              <div className="flex items-center gap-2 px-3 py-1.5 text-xs">
                <CloudCheck size={14} weight="fill" className="text-[#00994C]" />
                <span className="text-[#00994C] font-medium">Drive подключён</span>
              </div>
            ) : (
              <div>
                <div className="flex items-center gap-2 px-3 py-1.5 text-xs">
                  <CloudSlash size={14} weight="fill" className="text-[#52525b]" />
                  <span className="text-[#52525b] font-medium">Drive не подключён</span>
                </div>
                <Link
                  to="/connect"
                  data-testid="sidebar-connect"
                  className="mt-1 w-full flex items-center justify-center gap-2 px-3 py-2 text-sm bg-[#0047FF] text-white hover:bg-[#0036CC] transition-colors duration-150"
                >
                  Подключить
                </Link>
              </div>
            )}
          </div>
        )}

        {/* User info + logout */}
        <div className="border-t border-[#d4d4d8] p-3" data-testid="sidebar-user">
          <div className="flex items-center gap-2 px-3 py-1.5 text-xs">
            <User size={14} weight="bold" className="text-[#52525b]" />
            <span className="font-mono">{user?.username}</span>
          </div>
          <button
            onClick={logout}
            data-testid="sidebar-logout"
            className="mt-1 w-full flex items-center gap-2 px-3 py-2 text-sm border border-[#18181b] bg-transparent hover:bg-[#18181b] hover:text-white transition-colors duration-150"
          >
            <SignOut size={14} weight="bold" />
            <span>Выйти</span>
          </button>
          {isAdmin && (
            <button
              onClick={() => setConfirmRevokeAll(true)}
              data-testid="sidebar-logout-all"
              title="Отзывает все токены этого аккаунта (на всех устройствах)"
              className="mt-1 w-full flex items-center gap-2 px-3 py-2 text-xs text-[#52525b] hover:text-[#FF2A00] transition-colors duration-150"
            >
              <ShieldCheck size={12} weight="bold" />
              <span>Выйти со всех устройств</span>
            </button>
          )}
        </div>
      </aside>

      {confirmRevokeAll && (
        <ConfirmDialog
          state={{
            title: "Выйти со всех устройств?",
            description: "Все активные сессии этого аккаунта станут невалидными. Вам придётся войти заново. Используйте, если подозреваете, что токен был скомпрометирован (украденный ноутбук, утечка).",
            confirmLabel: "Отозвать все",
            cancelLabel: "Отмена",
            tone: "danger",
            onConfirm: () => logoutAllDevices(),
          }}
          onClose={() => setConfirmRevokeAll(false)}
        />
      )}

      {/* Main */}
      <main className="flex-1 min-w-0">
        <Outlet />
      </main>
    </div>
  );
}
